// src/services/textOptimization/TextOptimizationService.ts

import { EmailVariables } from '../email/types';
import { EmailPreviewService } from '../email/EmailPreviewService';
import { netlifyFetch } from '../../shared/services/netlifyFetch';

export interface OptimizationResult {
  success: boolean;
  optimizedText?: string;
  error?: string;
}

export interface OptimizationContext {
  emailVariables?: EmailVariables;
  attachments?: Array<{ filename: string; size?: number }>;
}

export class TextOptimizationService {
  private apiEndpoint = '/.netlify/functions/optimize-text';
  private emailPreviewService: EmailPreviewService;

  constructor() {
    this.emailPreviewService = new EmailPreviewService();
  }

  /**
   * Optimiert den eingegebenen Text für professionelle Steuerberater-E-Mails
   * ERWEITERT: Jetzt mit vollständigem E-Mail-Kontext für bessere Anrede-Erkennung
   * UND Namen-Formatierung durch OpenAI
   */
  async optimizeText(
    inputText: string, 
    context?: OptimizationContext
  ): Promise<OptimizationResult> {
    try {
      if (!inputText || inputText.trim().length < 10) {
        return {
          success: false,
          error: 'Text ist zu kurz. Bitte geben Sie mindestens 10 Zeichen ein.'
        };
      }

      // Bestimme Optimierungsmodus basierend auf verfügbarem Kontext
      const hasContext = context?.emailVariables;
      const optimizationMode = hasContext ? 'in-context' : 'standalone';

      let requestBody: any = {
        text: inputText,
        optimizationMode
      };

      // Wenn E-Mail-Kontext verfügbar ist, erstelle vollständige E-Mail mit Markierungen
      if (hasContext && context?.emailVariables) {
        try {
          // Generiere E-Mail-Vorschau mit den verfügbaren Variablen
          const emailPreview = this.emailPreviewService.generatePreview(
            context.emailVariables,
            context.attachments
          );

          // Extrahiere Text-Version der E-Mail
          const fullEmailText = this.emailPreviewService.extractTextFromHtml(
            emailPreview.bodyText
          );

          // Erstelle markierten Text für OpenAI
          const { contextText } = this.emailPreviewService.prepareTextForOptimization(
            fullEmailText,
            inputText
          );

          requestBody.fullEmailContext = contextText;

          console.log('Optimierung mit vollständigem E-Mail-Kontext:', {
            mode: optimizationMode,
            hasAnrede: !!context.emailVariables.anrede,
            hasVorname: !!context.emailVariables.vorname,
            hasNachname: !!context.emailVariables.nachname,
            hasGeschlecht: !!context.emailVariables.geschlecht
          });

        } catch (contextError) {
          console.warn('Fehler beim Erstellen des E-Mail-Kontexts, fallback zu standalone:', contextError);
          // Fallback zu standalone-Modus bei Kontext-Fehlern
          requestBody.optimizationMode = 'standalone';
        }
      }

      const response = await netlifyFetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Optimierung fehlgeschlagen: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log(`Textoptimierung erfolgreich (${result.mode || optimizationMode}):`, {
          originalLength: inputText.length,
          optimizedLength: result.optimizedText?.length || 0,
          hasNames: result.optimizedText?.includes('**') || false
        });

        return {
          success: true,
          optimizedText: result.optimizedText
        };
      } else {
        return {
          success: false,
          error: result.error || 'Unbekannter Fehler bei der Textoptimierung'
        };
      }

    } catch (error) {
      console.error('Text optimization error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Fehler bei der Textoptimierung'
      };
    }
  }

  /**
   * Optimiert Text mit vollem E-Mail-Kontext (Convenience-Methode)
   */
  async optimizeTextWithEmailContext(
    inputText: string,
    emailVariables: EmailVariables,
    attachments?: Array<{ filename: string; size?: number }>
  ): Promise<OptimizationResult> {
    return this.optimizeText(inputText, {
      emailVariables,
      attachments
    });
  }

  /**
   * Legacy-Methode für Backwards-Compatibility
   * Optimiert Text ohne Kontext (standalone-Modus)
   */
  async optimizeTextStandalone(inputText: string): Promise<OptimizationResult> {
    return this.optimizeText(inputText);
  }

  /**
   * Gibt Stil-Hinweise für den Benutzer
   */
  getStyleHints(): string[] {
    return [
      'Professioneller, aber freundlicher Ton',
      'Klare und prägnante Formulierungen',
      'Höfliche Ansprache',
      'Fokus auf relevante Informationen',
      'Namen werden automatisch fett formatiert' // NEU
    ];
  }

  /**
   * Validiert ob der Input-Text für die Optimierung geeignet ist
   */
  validateInputText(text: string): { isValid: boolean; error?: string } {
    if (!text || text.trim().length === 0) {
      return { isValid: false, error: 'Text darf nicht leer sein' };
    }

    if (text.trim().length < 10) {
      return { isValid: false, error: 'Text ist zu kurz (mindestens 10 Zeichen)' };
    }

    if (text.length > 2000) {
      return { isValid: false, error: 'Text ist zu lang (maximal 2000 Zeichen)' };
    }

    return { isValid: true };
  }
}

// Singleton instance
export const textOptimizationService = new TextOptimizationService();
